<script setup lang="ts">
import { ref, computed } from 'vue';
import { UiText, UiInputField, UiButton } from '@shopexperts/shared';
import { useCategoriesManager } from '@/app/composables/categories-manager/useCategoriesManager';
import CategoryTree from '@/app/components/categories-manager/CategoryTree.vue';
import CategoryDialog from '@/app/components/categories-manager/CategoryDialog.vue';
import DeleteConfirmDialog from '@/app/components/categories-manager/DeleteConfirmDialog.vue';
import LoadingCard from '@/components/common/LoadingCard.vue';
import type { AdminCategory, AdminSubcategory } from '@shopexperts/legacy-api';
import type { DialogMode } from '@/app/components/categories-manager/CategoryDialog.vue';
import type { DeleteTarget } from '@/app/components/categories-manager/DeleteConfirmDialog.vue';

const searchQuery = ref('');

const {
  categories,
  isLoading,
  createCategory,
  updateCategory,
  deleteCategory,
  createSubcategory,
  updateSubcategory,
  deleteSubcategory,
} = useCategoriesManager();

const totalCategories = computed(() => categories.value?.length ?? 0);
const totalSubcategories = computed(() =>
  categories.value?.reduce((sum, cat) => sum + cat.subcategories.length, 0) ?? 0
);

// --- Dialog state ---
const dialogOpen = ref(false);
const dialogLoading = ref(false);
const dialogMode = ref<DialogMode>('create-category');
const dialogInitialName = ref('');
const dialogInitialGroupName = ref<string | null>(null);
const dialogInitialCategoryId = ref<number | undefined>(undefined);
const editingCategoryId = ref<number | undefined>(undefined);
const editingSubcategoryId = ref<number | undefined>(undefined);

function openCreateCategory() {
  dialogMode.value = 'create-category';
  dialogInitialName.value = '';
  dialogInitialGroupName.value = null;
  dialogInitialCategoryId.value = undefined;
  editingCategoryId.value = undefined;
  editingSubcategoryId.value = undefined;
  dialogOpen.value = true;
}

function openEditCategory(category: AdminCategory) {
  dialogMode.value = 'edit-category';
  dialogInitialName.value = category.name;
  dialogInitialGroupName.value = category.group_name;
  dialogInitialCategoryId.value = undefined;
  editingCategoryId.value = category.id;
  editingSubcategoryId.value = undefined;
  dialogOpen.value = true;
}

function openAddSubcategory(parentCategory: AdminCategory) {
  dialogMode.value = 'create-subcategory';
  dialogInitialName.value = '';
  dialogInitialGroupName.value = null;
  dialogInitialCategoryId.value = parentCategory.id;
  editingCategoryId.value = undefined;
  editingSubcategoryId.value = undefined;
  dialogOpen.value = true;
}

function openEditSubcategory(subcategory: AdminSubcategory & { category_id: number }) {
  dialogMode.value = 'edit-subcategory';
  dialogInitialName.value = subcategory.name;
  dialogInitialGroupName.value = null;
  dialogInitialCategoryId.value = subcategory.category_id;
  editingCategoryId.value = undefined;
  editingSubcategoryId.value = subcategory.id;
  dialogOpen.value = true;
}

async function handleDialogSubmit(data: { name: string; groupName?: string | null; categoryId?: number }) {
  dialogLoading.value = true;
  try {
    switch (dialogMode.value) {
      case 'create-category':
        await createCategory.mutateAsync({ name: data.name, groupName: data.groupName ?? null });
        break;
      case 'edit-category':
        if (editingCategoryId.value != null) {
          await updateCategory.mutateAsync({
            id: editingCategoryId.value,
            name: data.name,
            groupName: data.groupName ?? null,
          });
        }
        break;
      case 'create-subcategory':
        if (data.categoryId != null) {
          await createSubcategory.mutateAsync({ categoryId: data.categoryId, name: data.name });
        }
        break;
      case 'edit-subcategory':
        if (editingSubcategoryId.value != null && data.categoryId != null) {
          await updateSubcategory.mutateAsync({
            id: editingSubcategoryId.value,
            categoryId: data.categoryId,
            name: data.name,
          });
        }
        break;
    }
    dialogOpen.value = false;
  } finally {
    dialogLoading.value = false;
  }
}

// --- Delete dialog state ---
const deleteDialogOpen = ref(false);
const deleteDialogLoading = ref(false);
const deleteTarget = ref<DeleteTarget>('category');
const deleteTargetName = ref('');
const deleteTargetId = ref<number>(0);
const deleteSubcategoryCount = ref<number>(0);

function openDeleteCategory(category: AdminCategory) {
  deleteTarget.value = 'category';
  deleteTargetName.value = category.name;
  deleteTargetId.value = category.id;
  deleteSubcategoryCount.value = category.subcategories.length;
  deleteDialogOpen.value = true;
}

function openDeleteSubcategory(subcategory: AdminSubcategory & { category_id: number }) {
  deleteTarget.value = 'subcategory';
  deleteTargetName.value = subcategory.name;
  deleteTargetId.value = subcategory.id;
  deleteSubcategoryCount.value = 0;
  deleteDialogOpen.value = true;
}

async function handleDeleteConfirm() {
  deleteDialogLoading.value = true;
  try {
    if (deleteTarget.value === 'category') {
      await deleteCategory.mutateAsync(deleteTargetId.value);
    } else {
      await deleteSubcategory.mutateAsync(deleteTargetId.value);
    }
    deleteDialogOpen.value = false;
  } finally {
    deleteDialogLoading.value = false;
  }
}
</script>

<template>
  <main class="flex-1 p-8 overflow-y-auto bg-secondary font-light">
    <!-- Header -->
    <div class="flex flex-col md:flex-row md:items-start justify-between mb-6 gap-4">
      <div>
        <UiText as="h1" variant="h3" text="**Categories Manager**" />
        <UiText as="p" variant="p-small" class="text-gray-500 mt-1">
          Manage service categories and subcategories
        </UiText>
      </div>
      <div class="flex items-center gap-3">
        <UiText as="span" variant="p-small" class="text-gray-400 tabular-nums whitespace-nowrap">
          {{ totalCategories }} categor{{ totalCategories !== 1 ? 'ies' : 'y' }},
          {{ totalSubcategories }} subcategor{{ totalSubcategories !== 1 ? 'ies' : 'y' }}
        </UiText>
      </div>
    </div>

    <!-- Search + Add -->
    <div class="mb-6">
      <div class="flex items-center gap-3">
        <UiInputField
          v-model="searchQuery"
          placeholder="Search categories..."
          icon="search"
          size="md"
          class="max-w-sm flex-1"
        />
        <UiButton
          label="Add Category"
          variant="primary"
          @click="openCreateCategory"
        />
      </div>
    </div>

    <!-- Loading -->
    <LoadingCard v-if="isLoading" />

    <!-- Tree -->
    <CategoryTree
      v-else-if="categories"
      :categories="categories"
      :search-query="searchQuery"
      @edit-category="openEditCategory"
      @delete-category="openDeleteCategory"
      @add-subcategory="openAddSubcategory"
      @edit-subcategory="openEditSubcategory"
      @delete-subcategory="openDeleteSubcategory"
    />

    <!-- Category Create/Edit Dialog -->
    <CategoryDialog
      v-model="dialogOpen"
      v-model:loading="dialogLoading"
      :mode="dialogMode"
      :categories="categories ?? []"
      :initial-name="dialogInitialName"
      :initial-group-name="dialogInitialGroupName"
      :initial-category-id="dialogInitialCategoryId"
      @submit="handleDialogSubmit"
    />

    <!-- Delete Confirmation Dialog -->
    <DeleteConfirmDialog
      v-model="deleteDialogOpen"
      v-model:loading="deleteDialogLoading"
      :target="deleteTarget"
      :name="deleteTargetName"
      :subcategory-count="deleteSubcategoryCount"
      @confirm="handleDeleteConfirm"
    />
  </main>
</template>
